import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertCircle, MailX, Loader2 } from "lucide-react";
import { SiteShell } from "@/components/layout/SiteShell";

/**
 * One-click unsubscribe landing page.
 *
 * Reads ``?token=<jwt>`` from the URL, verifies it against the public
 * ``/api/unsubscribe/verify`` endpoint (read-only — safe for email-link
 * scanners), and shows a single confirm button. Only when the user actually
 * taps the button do we POST ``/api/unsubscribe/confirm`` — which flips
 * their opt-in to False, records the source as ``email_link``, and keeps
 * their account fully active.
 */
export default function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("loading"); // loading | ready | already | confirming | done | error
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    async function verify() {
      if (!token) {
        setError("This unsubscribe link is missing its token.");
        setStatus("error");
        return;
      }
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/unsubscribe/verify`;
        const { data } = await axios.get(url, { params: { token }, withCredentials: false });
        if (!alive) return;
        setEmail(data.email || "");
        setStatus(data.already_opted_out ? "already" : "ready");
      } catch (e) {
        if (!alive) return;
        setError(e?.response?.data?.detail || "This link is no longer valid.");
        setStatus("error");
      }
    }
    verify();
    return () => { alive = false; };
  }, [token]);

  const confirm = async () => {
    setStatus("confirming");
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/unsubscribe/confirm`;
      await axios.post(url, { token }, { withCredentials: false });
      setStatus("done");
    } catch (e) {
      setError(e?.response?.data?.detail || "We couldn't complete the unsubscribe. Please try again.");
      setStatus("error");
    }
  };

  return (
    <SiteShell>
      <section className="container-page py-16 md:py-24" data-testid="unsubscribe-page">
        <div className="mx-auto max-w-lg text-center">
          {(status === "loading" || status === "confirming") && (
            <>
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <Loader2 size={22} className="animate-spin text-muted-foreground" />
              </div>
              <h1 className="mt-6 font-serif text-3xl">
                {status === "loading" ? "Checking your link…" : "Updating your preferences…"}
              </h1>
            </>
          )}

          {status === "ready" && (
            <>
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--sb-clay))]/15">
                <MailX size={22} className="text-[hsl(var(--sb-clay))]" />
              </div>
              <h1 className="mt-6 font-serif text-3xl leading-tight" data-testid="unsubscribe-ready-heading">
                Unsubscribe from broadcast emails?
              </h1>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                We’ll stop sending broadcast updates to{" "}
                <span className="font-medium text-foreground">{email}</span>.
                Your Last Bookmark account stays active and you’ll still receive
                essential transactional emails (like password resets or manuscript access notices).
              </p>
              <button
                type="button"
                onClick={confirm}
                className="btn-primary mt-8"
                data-testid="unsubscribe-confirm-btn"
              >
                Confirm unsubscribe
              </button>
              <div className="mt-4 text-xs text-muted-foreground">
                Changed your mind?{" "}
                <Link to="/settings" className="underline hover:no-underline">Manage all email preferences</Link>.
              </div>
            </>
          )}

          {status === "already" && (
            <>
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <CheckCircle2 size={22} className="text-muted-foreground" />
              </div>
              <h1 className="mt-6 font-serif text-3xl" data-testid="unsubscribe-already-heading">
                You’re already unsubscribed.
              </h1>
              <p className="mt-3 text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{email}</span> is not currently on our broadcast list.
              </p>
              <Link to="/settings" className="btn-ghost border mt-8 inline-flex" data-testid="unsubscribe-manage-btn">
                Manage email preferences
              </Link>
            </>
          )}

          {status === "done" && (
            <>
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--sb-forest))]/15">
                <CheckCircle2 size={22} className="text-[hsl(var(--sb-forest-2))]" />
              </div>
              <h1 className="mt-6 font-serif text-3xl" data-testid="unsubscribe-done-heading">
                You’re unsubscribed.
              </h1>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                We won’t send any more broadcast updates to{" "}
                <span className="font-medium text-foreground">{email}</span>.
                Your Last Bookmark account is still active — sign in any time to keep
                using the site or opt back in from your settings.
              </p>
              <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
                <Link to="/settings" className="btn-ghost border">Manage preferences</Link>
                <Link to="/" className="btn-primary">Back to homepage</Link>
              </div>
            </>
          )}

          {status === "error" && (
            <>
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--sb-clay))]/15">
                <AlertCircle size={22} className="text-[hsl(var(--sb-clay))]" />
              </div>
              <h1 className="mt-6 font-serif text-3xl" data-testid="unsubscribe-error-heading">
                We couldn’t use this link.
              </h1>
              <p className="mt-3 text-sm text-muted-foreground" data-testid="unsubscribe-error-detail">
                {error}
              </p>
              <p className="mt-4 text-sm text-muted-foreground">
                You can opt out any time from{" "}
                <Link to="/settings" className="underline hover:no-underline">Settings → Email Preferences</Link>.
              </p>
            </>
          )}
        </div>
      </section>
    </SiteShell>
  );
}
